import { ChangeDetectionStrategy, Component, Type, inject, signal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { ESplashLoaderComponent } from '@shared/components/loaders/e-splash-loader/e-splash-loader.component';
import { HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';

type LineTextReviewContentModule = { HdLineTextReviewContentComponent: Type<unknown> };

/**
 * Dunne paneel-schil voor het reviewoverzicht van de eigen lijnteksten: de
 * echte weergave komt via een dynamische import zodra het paneel opengaat
 * (reviewers zijn een handvol gebruikers; de rest laadt die code nooit).
 */
@Component({
  selector: 'hd-line-text-review-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, ESplashLoaderComponent],
  styleUrl: './hd-line-text-review-panel.component.scss',
  template: `
    @if (content(); as contentComponent) {
      <ng-container [ngComponentOutlet]="contentComponent" />
    } @else if (loadFailed()) {
      <div class="ltr-panel__state" role="alert">
        <p>{{ t('humanDesign.chart.lineTextReview.loadError') }}</p>
        <button type="button" class="ltr-panel__retry" (click)="retry()">
          {{ t('humanDesign.chart.lineTextReview.retry') }}
        </button>
      </div>
    } @else {
      <div class="ltr-panel__state" role="status" [attr.aria-label]="t('humanDesign.chart.lineTextReview.loading')">
        <e-splash-loader [size]="40" />
      </div>
    }
  `,
})
export class HdLineTextReviewPanelComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly content = signal<Type<unknown> | undefined>(undefined);
  protected readonly loadFailed = signal(false);

  /** Overschrijfbaar in specs; de echte import levert de lazy chunk. */
  protected loadContent: () => Promise<LineTextReviewContentModule> = () =>
    import('./hd-line-text-review-content.component');

  constructor() {
    void this.ensureContent();
  }

  protected retry(): void {
    void this.ensureContent();
  }

  protected t(key: string): string {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, undefined, this.contentLang.effectiveLang());
  }

  private async ensureContent(): Promise<void> {
    this.loadFailed.set(false);
    try {
      const contentModule = await this.loadContent();
      this.content.set(contentModule.HdLineTextReviewContentComponent);
    } catch {
      this.loadFailed.set(true);
    }
  }
}
